const _excluded = ["identifier", "__isNew__"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import { filter, find, has, isArray, isEmpty, isObject, pluck, sortBy } from 'underscore';
import PropTypes from 'prop-types';
import moment from 'moment';
import React from 'react';
import MultiPicker from '../../common/controls/multiPicker';
import { leafPedigreesString } from '../../../../behaviors/src/lib/common';
import { removeNulls } from '../../../utils/contentTools';
import localChangeSelector from '../selectors/localChangeSelector';

/**
 * Note that this is not a connected component. optionPickerField is connected and it's the
 * only code that creates OptionMultiPicker
 */
/* eslint-disable no-underscore-dangle */
const OptionMultiPicker = ({
  allowsOther: _allowsOther = false,
  allowsMultipleSelection: _allowsMultipleSelection = true,
  className: _className = '',
  contentPath,
  enabled,
  labelKey: _labelKey = 'title',
  options,
  valueKey: _valueKey = 'identifier',
  values: _values = '',
  path,
  onChange,
  onTextChange: _onTextChange = null,
  endMessage: _endMessage = null,
  testingIdentifier: _testingIdentifier = ''
}) => {
  const localChangeFunc = localChangeSelector({}, {
    allowsOther: _allowsOther,
    allowsMultipleSelection: _allowsMultipleSelection,
    contentPath,
    className: _className,
    enabled,
    labelKey: _labelKey,
    options,
    valueKey: _valueKey,
    values: _values,
    path,
    onChange,
    testingIdentifier: _testingIdentifier
  });
  const handleChange = (incomingValues = []) => {
    if (incomingValues.length === 0) {
      return onChange(localChangeFunc({
        path,
        value: null
      }));
    }
    const changes = {};
    incomingValues.forEach(value => {
      const {
          identifier,
          __isNew__
        } = value,
        rest = _objectWithoutProperties(value, _excluded);
      if (!rest._created) {
        rest._created = new Date().toISOString();
      }
      changes[identifier] = rest;
    });
    if (Object.keys(changes).length === 0) {
      return onChange(localChangeFunc({
        path,
        value: null
      }));
    }
    // removeNulls mutates value...
    const c = JSON.parse(JSON.stringify(changes));
    const displayValue = leafPedigreesString(removeNulls(c));
    return onChange(localChangeFunc({
      path,
      displayValue,
      value: changes
    }));
  };

  /**
   *  @function prepareValues - transforms data coming from server.
   */
  const prepareValues = () => {
    let transformedValues = _values;
    let ids = [];
    if (isObject(_values) && isEmpty(_values)) {
      transformedValues = [];
    }
    if (isObject(_values) && Object.keys(_values).length) {
      transformedValues = [];
      Object.keys(_values).forEach(key => {
        if (_values[key] !== null) {
          // do not display items with null values.
          transformedValues.push({
            [_labelKey]: _values[key][_labelKey],
            [_valueKey]: key,
            _created: _values[key]._created
          });
        }
      });
      transformedValues = sortBy(transformedValues, o => {
        const x = moment(o._created, 'x');
        return x.isValid() ? x : o._created;
      });
      ids = pluck(transformedValues, _valueKey);
    }
    return {
      ids,
      values: transformedValues
    };
  };

  /**
   * @function prepareOptions - sorts options by _created field;
   *
   */
  const prepareOptions = () => {
    // check options array for existence of valueKey and labelKey fields.
    // if the valueKey field (ex. identifier) is missing,
    // create a valueKey field and assign it a value derived from labelKey.
    //
    // Also add a _created property to each option
    // based its order in the options array.
    const rawOptions = JSON.parse(JSON.stringify(options));
    let newOption = {};
    const sortedOptions = [];
    rawOptions.forEach((option, index) => {
      newOption = option;
      if (!has(newOption, '_created')) {
        newOption._created = index + 1;
      }
      if (!has(newOption, _valueKey)) {
        newOption[_valueKey] = `_${newOption[_labelKey]}`;
      }
      sortedOptions.push(newOption);
    });
    return sortedOptions;
  };

  /**
   * @function prepareOtherOptions - adds an "other" value to the
   *                      options list so it may be shown as selected.
   *                      assign it a _created property equal to utc date.
   */
  const prepareOtherOptions = () => {
    let optionsWithOther = prepareOptions();
    const preparedValues = prepareValues(_values);
    const otherValues = preparedValues.values;

    // handles adding in "other" value to the options list
    if (isArray(otherValues) && otherValues.length > 0) {
      // include the value if it is NOT found in the option list.
      const valuesNotInOptions = filter(otherValues, val => !find(optionsWithOther, option => option[_valueKey] === val[_valueKey]));
      valuesNotInOptions.forEach(value => {
        optionsWithOther = optionsWithOther.concat({
          [_valueKey]: value[_valueKey],
          [_labelKey]: value[_labelKey],
          _created: value._created ? value._created : moment().toISOString()
        });
      });
    }
    return optionsWithOther;
  };
  const newProps = _objectSpread(_objectSpread({}, {
    allowsOther: _allowsOther,
    allowsMultipleSelection: _allowsMultipleSelection,
    contentPath,
    className: _className,
    enabled,
    labelKey: _labelKey,
    options,
    valueKey: _valueKey,
    values: _values,
    path,
    onChange,
    onTextChange: _onTextChange,
    endMessage: _endMessage,
    testingIdentifier: _testingIdentifier
  }), {}, {
    values: prepareValues().ids,
    multi: true,
    onChange: handleChange,
    disabled: !enabled,
    verticalPadding: false
  });
  if (_allowsOther) {
    newProps.allowCreate = true;
    newProps.options = prepareOtherOptions();
    newProps.className += _testingIdentifier !== '' ? ` t-omp t-omp-${_testingIdentifier} ` : '';
  } else {
    newProps.options = prepareOptions();
    newProps.className += _testingIdentifier !== '' ? ` t-omp t-omp-${_testingIdentifier} ` : '';
  }
  return /*#__PURE__*/React.createElement(MultiPicker, newProps);
};
OptionMultiPicker.propTypes = {
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  className: PropTypes.string,
  contentPath: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
  path: PropTypes.string.isRequired,
  labelKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onTextChange: PropTypes.func,
  endMessage: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  testingIdentifier: PropTypes.string,
  valueKey: PropTypes.string,
  values: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({}), PropTypes.array])
};
export default OptionMultiPicker;